/**
 * outcomeMeta descriptions are `key:value|key:value` lists for templated outcomes, and free text (or "other", or
 * empty) for the rest. No imports: hl/outcomes.ts re-exports parseDescription from here.
 */

const KEY = /^[A-Za-z][A-Za-z0-9_]*$/;

/**
 * `key:value|key:value` into a map. Robust to odd input:
 *  - values may contain ':' ("perp:xyz:XYZ100", "officialSource:realityArbSepolia: 0x05…");
 *  - a segment that does not start with an identifier key is a '|' inside the previous value and is glued back on;
 *  - keys and values are trimmed; empty keys, empty segments and leading junk are dropped; the first copy of a
 *    repeated key wins.
 * Free text ("This outcome resolves to Yes if …") yields {} or at most the pairs it really contains.
 */
export function parseDescription(description: string | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof description !== 'string' || description === '') return out;
  let last: string | null = null;
  for (const part of description.split('|')) {
    const i = part.indexOf(':');
    const key = i > 0 ? part.slice(0, i).trim() : '';
    if (key && KEY.test(key)) {
      if (Object.prototype.hasOwnProperty.call(out, key)) {
        last = null; // ignore repeats (and anything glued to them)
        continue;
      }
      out[key] = part.slice(i + 1).trim();
      last = key;
    } else if (last !== null && part !== '') {
      out[last] = `${out[last]}|${part}`.trim();
    }
  }
  return out;
}

/** True when the description is a non-empty `key:value|…` list (every segment parses as a pair). */
export function isKeyValueDescription(description: string | null | undefined): boolean {
  if (typeof description !== 'string' || !description.includes(':')) return false;
  const parts = description.split('|').filter((p) => p !== '');
  if (parts.length === 0) return false;
  // A lone "Word: text" is prose; a lone "index:2" / "participant:Buffalo Bills" is a pair.
  if (parts.length === 1 && /^[^:]*:\s/.test(parts[0] ?? '')) return false;
  return parts.every((p) => {
    const i = p.indexOf(':');
    return i > 0 && KEY.test(p.slice(0, i).trim());
  });
}
